import React, { useEffect, useMemo, useState, useRef } from 'react';
import { CalendarIcon, UsersIcon, UserIcon, BriefcaseIcon, TrendingUpIcon, ChevronLeftIcon, ChevronRightIcon, PlusIcon, ClockIcon, BarChart3Icon, ChevronRightIcon as ArrowRightIcon, ListIcon, DownloadIcon, FileSpreadsheetIcon, TargetIcon, FileTextIcon } from 'lucide-react';
import { BarChart, Bar, AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Card, CardHeader, CardTitle, CardContent } from '../ui/Card';
import { Badge, getStatusBadgeVariant } from '../ui/Badge';
import { Button } from '../ui/Button';
import { SortableHeader } from '../ui/SortableHeader';
import { useData } from '../../contexts/DataContext';
import { getAppointmentOfferInfo } from '../../utils/appointmentPricing';
import { formatCurrency, formatTime, getToday, getTomorrow, getDaysInMonth, getFirstDayOfMonth, getMonthName, getStatusName, getDoctorPrefix } from '../../utils/helpers';
import { hasPermission } from '../../types';
import { useAuth } from '../../contexts/AuthContext';
import { DashboardRevenueByUser, DashboardRevenueByDoctor } from './DashboardRevenue';
import { DashboardAppointmentsByUser } from './DashboardAppointmentsByUser';
import { DashboardProductRevenue } from './DashboardProducts';
import { exportServiceDetailToPdf, exportDashboardChartDataToPdf, exportDashboardRevenueToPdf } from '../../utils/pdfExport';
import { exportDashboardChartDataToExcel, exportDashboardRevenueToExcel } from '../../utils/excelExport';
import { useSortableData } from '../../hooks/useSortableData';
import { useTheme } from '../../contexts/ThemeContext';
import { supabase } from '../../utils/supabase';
import { ExcelExportModal } from '../ui/ExcelExportModal';
import { PdfExportModal } from '../ui/PdfExportModal';
interface DashboardProps {
  onNavigate: (module: string) => void;
}
export function Dashboard({
  onNavigate
}: DashboardProps) {
  const {
    patients,
    doctors,
    appointments,
    services,
    opportunities,
    contacts,
    getPatient,
    getDoctor,
    getService,
    getSpecialty,
    getAppointmentsByDate,
    getTodayAppointmentsCount,
    getTotalOpportunitiesAmount,
    getActivePackageDiscount,
    getActiveOfferForService
  } = useData();
  // Misma fuente de verdad que el módulo de Citas.
  const pricingDeps = {
    services,
    getPatient,
    getActivePackageDiscount,
    getActiveOfferForService
  };
  const {
    user
  } = useAuth();
  const {
    theme
  } = useTheme();
  const isDark = theme === 'dark';
  const chartTooltipBg = isDark ? '#1e293b' : '#ffffff';
  const chartTooltipColor = isDark ? '#ffffff' : '#111827';
  const chartTooltipBorder = isDark ? '1px solid #334155' : '1px solid #e5e7eb';
  const chartTooltipLabelColor = isDark ? '#cbd5e1' : '#6b7280';
  const chartGridColor = isDark ? '#374151' : '#e5e7eb';
  const userRole = user?.role || '';
  const normalize = (name: string) => name ? name.toLowerCase().trim().normalize('NFD').replace(/[\u0300-\u036f]/g, '') : '';
  const cleanRole = normalize(userRole);
  const canViewStats = hasPermission(userRole, 'dashboard_stats', 'read');
  const canViewCalendar = hasPermission(userRole, 'dashboard_calendar', 'read');
  const canViewPipeline = hasPermission(userRole, 'dashboard_pipeline', 'read');
  const canViewProducts = hasPermission(userRole, 'products', 'read');
  const getManualPermission = (moduleKey: string) => {
    try {
      const saved = localStorage.getItem('savidna-role-permissions');
      if (!saved) return false;
      const perms = JSON.parse(saved);
      const level = perms[moduleKey]?.[cleanRole];
      return level === 'read' || level === 'full';
    } catch (e) {
      return false;
    }
  };
  // Admin siempre puede. Los demás roles dependen del permiso
  // "Exportar Ingresos (PDF/Excel)" configurable en Configuración > Roles y Permisos.
  const canDownloadPdf = cleanRole === 'administrador' || hasPermission(userRole, 'dashboard_revenue_export', 'read') || getManualPermission('dashboard_revenue_export');
  // Detect if user is a doctor/terapeuta and find their doctor record
  const isDoctorUser = useMemo(() => {
    const role = userRole.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    return role === 'doctor' || role === 'terapeuta';
  }, [userRole]);
  const userDoctorId = useMemo(() => {
    if (!isDoctorUser || !user) return null;
    const matchedDoctor = doctors.find(d => d.email?.toLowerCase().trim() === user.email?.toLowerCase().trim());
    return matchedDoctor?.id || null;
  }, [isDoctorUser, user, doctors]);
  const canViewRevenueByUser = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_revenue_user', 'read') || getManualPermission('dashboard_revenue_user');
  const canViewRevenueByDoctor = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_revenue_doctor', 'read') || getManualPermission('dashboard_revenue_doctor');
  const canViewProductRevenue = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_product_revenue', 'read') || getManualPermission('dashboard_product_revenue');
  const canViewAppointmentsByUser = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_appointments_by_user', 'read') || getManualPermission('dashboard_appointments_by_user');
  const canViewRecentAppointments = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_recent_appointments', 'read') || getManualPermission('dashboard_recent_appointments');
  const canViewServiceDetail = cleanRole === 'administrador' || cleanRole === 'admin' || hasPermission(cleanRole, 'dashboard_service_detail', 'read') || getManualPermission('dashboard_service_detail');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(getToday());
  const [doctorDateFrom, setDoctorDateFrom] = useState<string>(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().split('T')[0];
  });
  const [doctorDateTo, setDoctorDateTo] = useState<string>(getToday());
  const today = getToday();
  const [isRevenueExportModalOpen, setIsRevenueExportModalOpen] = useState(false);
  const [isRevenuePdfExportModalOpen, setIsRevenuePdfExportModalOpen] = useState(false);
  const [isExportingRevenue, setIsExportingRevenue] = useState(false);
  const handleExportRevenue = async (exportDateFrom: string | null, exportDateTo: string | null, format: 'excel' | 'pdf') => {
    setIsExportingRevenue(true);
    try {
      // Si no se especifica un rango (exportar "todos los registros"),
      // no se debe filtrar por fecha en absoluto.
      const from = exportDateFrom || null;
      const to = exportDateTo || null;
      let salesQuery = supabase.from('product_sales').select('*');
      if (from) salesQuery = salesQuery.gte('date', from);
      if (to) salesQuery = salesQuery.lte('date', to);
      const {
        data: salesData
      } = await salesQuery;
      const {
        data: productsData
      } = await supabase.from('products').select('id, name');
      const productNameMap: Record<string, string> = {};
      (productsData || []).forEach((p: any) => {
        productNameMap[p.id] = p.name;
      });
      const filteredApts = appointments.filter(a => (!from || a.date >= from) && (!to || a.date <= to) && (a.status === 'completada' || a.status === 'completed'));
      const aptRows = filteredApts.map(apt => {
        const patient = getPatient(apt.patientId);
        const doctor = getDoctor(apt.doctorId);
        const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
        const serviceNames = serviceIds.map(id => getService(id)?.name).join(', ');
        const total = serviceIds.reduce((sum, id) => {
          const price = getService(id)?.price || 0;
          // El canje de fidelidad es gratis para el cliente pero no para la
          // clínica, así que el ingreso se registra al precio completo.
          if (apt.loyaltyCardId && apt.loyaltyServiceId === id) {
            return sum + price;
          }
          // Incluye paquete de sesiones (aunque se haya comprado DESPUÉS de
          // agendar), certificado congelado, override manual y ofertas en vivo.
          return sum + getAppointmentOfferInfo(pricingDeps, apt, id).finalPrice;
        }, 0);
        let note = '';
        if (apt.loyaltyCardId) note = 'Tarjeta de Fidelidad';else if (apt.giftCertificateId) note = 'Certificado de Regalo';
        return {
          date: apt.date,
          patient: patient?.name || 'Paciente',
          doctor: doctor?.name || 'Doctor',
          service: serviceNames,
          total,
          nota: note,
          colaborador: apt.createdByUserName || '—'
        };
      });
      const prodRows = (salesData || []).flatMap((sale: any) => {
        const patient = sale.patient_id ? getPatient(sale.patient_id) : null;
        const patientName = patient?.name || sale.client_name || 'Venta directa';
        const items = Array.isArray(sale.items) ? sale.items : [];
        if (items.length === 0) {
          return [{
            date: sale.date || sale.created_at?.slice(0, 10),
            patient: patientName,
            product: 'Venta de productos',
            total: Number(sale.total || 0),
            colaborador: sale.created_by_user_name || '—'
          }];
        }
        return items.map((item: any) => {
          const productName = productNameMap[item.productId] || item.productName || 'Producto';
          const subtotal = Number(item.subtotal || (item.unitPrice || 0) * (item.quantity || 0));
          return {
            date: sale.date || sale.created_at?.slice(0, 10),
            patient: patientName,
            product: `${productName} (x${item.quantity || 1})`,
            total: subtotal,
            colaborador: sale.created_by_user_name || '—'
          };
        });
      });
      const labelFrom = from || 'Inicio';
      const labelTo = to || 'Hoy';
      if (format === 'excel') {
        exportDashboardRevenueToExcel(aptRows, prodRows, labelFrom, labelTo);
      } else {
        exportDashboardRevenueToPdf(aptRows, prodRows, labelFrom, labelTo);
      }
    } catch (err) {
      console.error(err);
    } finally {
      setIsExportingRevenue(false);
      setIsRevenueExportModalOpen(false);
      setIsRevenuePdfExportModalOpen(false);
    }
  };
  const selectedDateAppointments = useMemo(() => {
    const allApts = getAppointmentsByDate(selectedDate);
    if (isDoctorUser && userDoctorId) {
      return allApts.filter(a => a.doctorId === userDoctorId);
    }
    return allApts;
  }, [selectedDate, isDoctorUser, userDoctorId, getAppointmentsByDate]);
  // KPIs
  const totalAppointments = appointments.length;
  const todayCount = getTodayAppointmentsCount();
  const activeDoctors = doctors.length;
  const totalServices = services.length;
  const totalContacts = contacts.length;
  const totalOpportunities = opportunities.length;
  // Appointments Statistics (last 7 days trend only)
  const appointmentStats = useMemo(() => {
    const last7Days = [];
    for (let i = 6; i >= 0; i--) {
      const date = new Date();
      date.setDate(date.getDate() - i);
      // Use timezone-aware date string instead of toISOString() which uses UTC
      const dateStr = date.toLocaleDateString('en-CA', {
        timeZone: 'America/Santo_Domingo'
      });
      const dayAppointments = appointments.filter(a => a.date === dateStr);
      last7Days.push({
        date: date.toLocaleDateString('es-ES', {
          weekday: 'short',
          day: 'numeric',
          timeZone: 'America/Santo_Domingo'
        }),
        total: dayAppointments.length,
        completadas: dayAppointments.filter(a => a.status === 'completada').length,
        programadas: dayAppointments.filter(a => a.status === 'programada').length,
        canceladas: dayAppointments.filter(a => a.status === 'cancelada').length
      });
    }
    return {
      last7Days
    };
  }, [appointments]);
  // Top Doctores por Citas - filtered by date range
  const doctorChartData = useMemo(() => {
    const filteredAppointments = appointments.filter(apt => {
      if (doctorDateFrom && apt.date < doctorDateFrom) return false;
      if (doctorDateTo && apt.date > doctorDateTo) return false;
      return true;
    });
    const byDoctor = filteredAppointments.reduce((acc, apt) => {
      const doctor = getDoctor(apt.doctorId);
      const doctorName = doctor?.name || 'Sin asignar';
      acc[doctorName] = (acc[doctorName] || 0) + 1;
      return acc;
    }, {} as Record<string, number>);
    return Object.entries(byDoctor).map(([name, count]) => ({
      name,
      citas: count
    })).sort((a, b) => b.citas - a.citas).slice(0, 5);
  }, [appointments, getDoctor, doctorDateFrom, doctorDateTo]);
  // Service Detail Statistics
  const serviceDetailStats = useMemo(() => {
    const completedAppointments = appointments.filter(a => a.status === 'completada');
    const serviceMap: Record<string, {
      name: string;
      count: number;
      totalRevenue: number;
    }> = {};
    completedAppointments.forEach(apt => {
      const serviceIds = apt.serviceIds && apt.serviceIds.length > 0 ? apt.serviceIds : [apt.serviceId].filter(Boolean);
      serviceIds.forEach(sId => {
        const service = getService(sId);
        if (service) {
          if (!serviceMap[service.id]) serviceMap[service.id] = {
            name: service.name,
            count: 0,
            totalRevenue: 0
          };
          serviceMap[service.id].count++;
          const discountPercent = Number(apt.serviceDiscounts?.[sId] || 0);
          serviceMap[service.id].totalRevenue += Math.max(0, service.price - service.price * (discountPercent / 100));
        }
      });
    });
    const grandTotalRevenue = Object.values(serviceMap).reduce((sum, s) => sum + s.totalRevenue, 0);
    const grandTotalCount = Object.values(serviceMap).reduce((sum, s) => sum + s.count, 0);
    const details = Object.values(serviceMap).map(s => ({
      ...s,
      avgPerAppointment: s.count > 0 ? s.totalRevenue / s.count : 0,
      percentOfTotal: grandTotalRevenue > 0 ? s.totalRevenue / grandTotalRevenue * 100 : 0
    })).sort((a, b) => b.totalRevenue - a.totalRevenue);
    return {
      details,
      grandTotalRevenue,
      grandTotalCount
    };
  }, [appointments, getService]);
  // Pipeline stages
  const pipelineStages = [{
    id: 'nuevo_lead',
    name: 'Nuevo Lead',
    color: 'from-blue-500 to-blue-600',
    bgLight: 'bg-blue-50 dark:bg-blue-900/20',
    textColor: 'text-blue-600 dark:text-blue-400'
  }, {
    id: 'contactado',
    name: 'Contactado',
    color: 'from-cyan-500 to-cyan-600',
    bgLight: 'bg-cyan-50 dark:bg-cyan-900/20',
    textColor: 'text-cyan-600 dark:text-cyan-400'
  }, {
    id: 'evaluacion_consulta',
    name: 'Evaluación / Consulta',
    color: 'from-purple-500 to-purple-600',
    bgLight: 'bg-purple-50 dark:bg-purple-900/20',
    textColor: 'text-purple-600 dark:text-purple-400'
  }, {
    id: 'cotizacion_enviada',
    name: 'Cotización Enviada',
    color: 'from-amber-500 to-amber-600',
    bgLight: 'bg-amber-50 dark:bg-amber-900/20',
    textColor: 'text-amber-600 dark:text-amber-400'
  }, {
    id: 'confirmado',
    name: 'Confirmado',
    color: 'from-emerald-500 to-emerald-600',
    bgLight: 'bg-emerald-50 dark:bg-emerald-900/20',
    textColor: 'text-emerald-600 dark:text-emerald-400'
  }, {
    id: 'cita_agendada',
    name: 'Cita Agendada',
    color: 'from-teal-500 to-teal-600',
    bgLight: 'bg-teal-50 dark:bg-teal-900/20',
    textColor: 'text-teal-600 dark:text-teal-400'
  }, {
    id: 'servicio_realizado',
    name: 'Servicio Realizado',
    color: 'from-green-500 to-green-600',
    bgLight: 'bg-green-50 dark:bg-green-900/20',
    textColor: 'text-green-600 dark:text-green-400'
  }];
  const getStageCount = (stage: string) => opportunities.filter(o => o.stage === stage).length;
  const getStageAmount = (stage: string) => opportunities.filter(o => o.stage === stage).reduce((sum, o) => sum + o.amount, 0);
  // Calendar
  const year = currentDate.getFullYear();
  const month = currentDate.getMonth();
  const daysInMonth = getDaysInMonth(year, month);
  const firstDay = getFirstDayOfMonth(year, month);
  const calendarDays: (number | null)[] = [];
  for (let i = 0; i < firstDay; i++) calendarDays.push(null);
  for (let i = 1; i <= daysInMonth; i++) calendarDays.push(i);
  const getDateString = (day: number) => `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
  const getAppointmentCountForDay = (day: number) => {
    const dateStr = getDateString(day);
    return appointments.filter(a => {
      if (a.date !== dateStr || a.status === 'cancelada') return false;
      if (isDoctorUser && userDoctorId && a.doctorId !== userDoctorId) return false;
      return true;
    }).length;
  };
  const prevMonth = () => setCurrentDate(new Date(year, month - 1, 1));
  const nextMonth = () => setCurrentDate(new Date(year, month + 1, 1));
  const recentAppointments = [...appointments].sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()).slice(0, 5);
  // Mapped recent appointments with resolved names for sorting
  const recentAppointmentsMapped = recentAppointments.map(apt => {
    const patient = getPatient(apt.patientId);
    const doctor = getDoctor(apt.doctorId);
    const service = getService(apt.serviceId);
    return {
      ...apt,
      patientName: patient?.name || '-',
      doctorName: doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : '-',
      serviceName: service?.name || '-'
    };
  });
  const {
    sortedItems: sortedRecentAppointments,
    requestSort: recentRequestSort,
    isSorted: recentIsSorted
  } = useSortableData(recentAppointmentsMapped);
  // Sortable service detail stats
  const {
    sortedItems: sortedServiceDetails,
    requestSort: serviceRequestSort,
    isSorted: serviceIsSorted
  } = useSortableData(serviceDetailStats.details);
  const formattedDate = new Date().toLocaleDateString('es-ES', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric'
  });
  const getStatusAccentColor = (status: string) => {
    switch (status) {
      case 'programada':
        return 'bg-teal-500';
      case 'completada':
        return 'bg-emerald-500';
      case 'cancelada':
        return 'bg-rose-500';
      case 'reprogramada':
        return 'bg-amber-500';
      default:
        return 'bg-gray-400';
    }
  };
  return <div className="space-y-6" data-id="element-2454" data-mp="2eaeykg423lwn">
      {/* Welcome Hero Banner */}
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-teal-600 via-teal-500 to-cyan-500 p-6 md:p-8 text-white shadow-xl text-white" data-id="element-2455" data-mp="2997na72pg4l8">
        <div className="absolute -top-10 -right-10 w-72 h-72 rounded-full opacity-20 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(255,255,255,0.6) 0%, transparent 65%)'
      }} data-id="element-2456" data-mp="24chbvx1uhx65" />
        
        <div className="absolute -bottom-16 -left-8 w-64 h-64 rounded-full opacity-15 pointer-events-none" style={{
        background: 'radial-gradient(circle, rgba(6,182,212,0.8) 0%, transparent 60%)'
      }} data-id="element-2457" data-mp="3tqvv61u3gru9" />
        
        {/* Patrón decorativo de spa (loto, hoja, gota, piedras zen) */}
        <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{
        opacity: 0.11
      }} aria-hidden="true" data-id="element-2458" data-mp="h2lkob2srx0z">
          
          <defs data-id="element-2459" data-mp="826xt7a6q9vd">
            <pattern id="spaPattern" width="160" height="160" patternUnits="userSpaceOnUse" patternTransform="rotate(6)" data-id="element-2460" data-mp="81x9rp8pacsb">
              
              <g fill="none" stroke="#ffffff" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-2461" data-mp="3q9wgp9dmxxmg">
                
                <g transform="translate(20,18)" data-id="element-2462" data-mp="1oit3qdg9w6yq">
                  <path d="M12 26 c-8-4-12-12-12-12 4-1 9 1 12 5 3-4 8-6 12-5 0 0-4 8-12 12z" data-id="element-2463" data-mp="1oj1ikavyhmck" />
                  <path d="M12 26 c-4-7-3-16-3-16 3 1 5 5 5 9" data-id="element-2464" data-mp="36fmz06ptffrl" />
                  <path d="M12 26 c4-7 3-16 3-16 -3 1 -5 5 -5 9" data-id="element-2465" data-mp="3beg358j7puf3" />
                </g>
                <g transform="translate(108,28)" data-id="element-2466" data-mp="3g67sjfqmtu3n">
                  <path d="M0 14 c0-10 8-14 16-14 0 10-6 16-16 14z" data-id="element-2467" data-mp="3g6rer8nbf4nh" />
                  <path d="M3 12 c4-3 8-6 11-9" data-id="element-2468" data-mp="3k6i02wu2oskm" />
                </g>
                <g transform="translate(28,104)" data-id="element-2469" data-mp="2nhk4jcei0qme">
                  <path d="M8 0 c5 7 8 11 8 15 a8 8 0 0 1 -16 0 c0-4 3-8 8-15z" data-id="element-2470" data-mp="2niv7xb417wcz" />
                </g>
                <g transform="translate(104,104)" data-id="element-2471" data-mp="2sgx14ov69c26">
                  <ellipse cx="12" cy="22" rx="14" ry="4.5" data-id="element-2472" data-mp="2wi8mujaxdd53" />
                  <ellipse cx="12" cy="12" rx="10" ry="4" data-id="element-2473" data-mp="2wk36luagmz7l" />
                  <ellipse cx="12" cy="4" rx="6.5" ry="3" data-id="element-2474" data-mp="31i4nq9obsogf" />
                </g>
              </g>
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#spaPattern)" data-id="element-2475" data-mp="brhwk0pan2u5" />
        </svg>
        <div className="absolute left-0 top-0 bottom-0 w-1.5 rounded-l-2xl bg-white/25" data-id="element-2476" data-mp="gpjg0mqmt465" />
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-cyan-400/20 rounded-full blur-2xl translate-y-1/2 -translate-x-1/4" data-id="element-2477" data-mp="2cso2rp2sqw9u" />
        <div className="absolute top-1/2 right-1/4 w-32 h-32 bg-teal-300/10 rounded-full blur-xl" data-id="element-2478" data-mp="28ugmswg5pyzp" />
        <div className="relative z-10" data-id="element-2479" data-mp="2mq2hnk92nd01">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-6" data-id="element-2480" data-mp="2hsk9vx6sj3tm">
            <div data-id="element-2481" data-mp="1u2y8d32y4cig">
              <div className="flex items-center gap-2 mb-2" data-id="element-2482" data-mp="1u4j4uy8tm5iy">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-6 h-6 text-teal-200" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-2483" data-mp="1p4eod87wxasq">
                  
                  <path d="M12 21c-4-4-8-6.5-8-10.5a4.5 4.5 0 0 1 9 0 4.5 4.5 0 0 1 9 0c0 4-4 6.5-8 10.5" data-id="element-2484" data-mp="1bb5df34bgh83" />
                  <path d="M12 6c0-2.5 2-4 4-4" data-id="element-2485" data-mp="16bsrckl9re6k" />
                  <path d="M12 6c0-2.5-2-4-4-4" data-id="element-2486" data-mp="3i10heoau9q3e" />
                </svg>
                <span className="text-white/75 text-sm font-medium" data-id="element-2487" data-mp="3mrp5rur826yb">
                  {formattedDate}
                </span>
              </div>
              <h1 className="text-3xl md:text-4xl font-bold mb-2" data-id="element-2488" data-mp="391ve74oc18kw">
                {user?.gender === 'femenino' ? 'Bienvenida' : 'Bienvenido'},{' '}
                {user?.name?.split(' ')[0] || 'Usuario'}
              </h1>
              <p className="text-white/75 text-sm font-medium" data-id="element-2489" data-mp="4k9grrbxtai7">
                Panel de control de Savidna
              </p>
              <div className="flex flex-wrap gap-3 mt-4" data-id="element-2490" data-mp="3rrbv32kbrdnh">
                <button onClick={() => onNavigate('appointments:today')} className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full border border-white/20 hover:bg-white/25 transition-colors cursor-pointer" data-id="element-2491" data-mp="3rqsbziixuj7v">
                  
                  <ClockIcon className="w-4 h-4" data-id="element-2492" data-mp="2lcawsq7tujdq" />
                  <span className="font-semibold" data-id="element-2493" data-mp="30l69k0hj8upt">{todayCount}</span>
                  <span className="text-white/75 text-sm font-medium" data-id="element-2494" data-mp="30mhcxz72g0ge">
                    citas hoy
                  </span>
                </button>
                <div className="inline-flex items-center gap-2 px-4 py-2 bg-white/15 backdrop-blur-sm rounded-full border border-white/20" data-id="element-2495" data-mp="2qvv36lm1645m">
                  <UsersIcon className="w-4 h-4" data-id="element-2496" data-mp="2mr582yz74tlr" />
                  <span className="font-semibold" data-id="element-2497" data-mp="3jjqxxks1hlzr">{patients.length}</span>
                  <span className="text-white/75 text-sm font-medium" data-id="element-2498" data-mp="3eknqxusi15p5">
                    pacientes
                  </span>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap gap-3" data-id="element-2499" data-mp="3akndodh4z0jo">
              {hasPermission(userRole, 'patients', 'full') && <Button variant="outline" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2501" data-mp="1z4ymtb0rhhdl" />} onClick={() => onNavigate('patients:new')} className="bg-white/10 border-white/30 text-white hover:bg-white/20 backdrop-blur-sm" data-id="element-2500" data-mp="1z3duzt5u0cjf">
                
                  Nuevo Paciente
                </Button>}
              {hasPermission(userRole, 'appointments', 'full') && <Button variant="ghost" leftIcon={<PlusIcon className="w-4 h-4" data-id="element-2503" data-mp="ndohqg2vcyp5" />} onClick={() => onNavigate('appointments:new')} style={{
              backgroundColor: '#ffffff',
              backgroundImage: 'none',
              color: '#0f766e'
            }} className="shadow-lg shadow-teal-900/20 hover:opacity-90" data-id="element-2502" data-mp="im5bujm2v2kr">
                
                  Nueva Cita
                </Button>}
            </div>
          </div>
        </div>
      </div>

      {/* KPI Cards */}
      {canViewStats && <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4" data-id="element-2504" data-mp="343vioha659oi">
          {[{
        icon: CalendarIcon,
        value: totalAppointments,
        label: 'Citas Totales',
        gradient: 'from-teal-500 to-cyan-500',
        module: 'appointments'
      }, {
        icon: ClockIcon,
        value: todayCount,
        label: 'Citas Hoy',
        gradient: 'from-emerald-500 to-green-500',
        module: 'appointments:today'
      }, {
        icon: UserIcon,
        value: activeDoctors,
        label: 'Doctores',
        gradient: 'from-purple-500 to-indigo-500',
        module: 'doctors'
      }, {
        icon: BriefcaseIcon,
        value: totalServices,
        label: 'Servicios',
        gradient: 'from-orange-500 to-amber-500',
        module: 'services'
      }, {
        icon: UsersIcon,
        value: totalContacts,
        label: 'Contactos',
        gradient: 'from-yellow-500 to-amber-500',
        module: 'contacts'
      }, {
        icon: TrendingUpIcon,
        value: totalOpportunities,
        label: 'Oportunidades',
        gradient: 'from-rose-500 to-pink-500',
        module: 'opportunities'
      }].map((kpi, index) => {
        const Icon = kpi.icon;
        return <Card key={index} className="cursor-pointer hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 overflow-hidden group" onClick={() => onNavigate(kpi.module)} data-id="element-2505" data-mp="1mlkwjqnfqlxs" data-mpi={index}>
              
                <div className={`absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b ${kpi.gradient}`} data-id="element-2506" data-mp="1ctnhu9x7xbub" />
              
                <div className="flex items-center gap-3 pl-3" data-id="element-2507" data-mp="12x2h40s8emwa">
                  <div className={`p-2.5 rounded-xl bg-gradient-to-br ${kpi.gradient} shadow-lg`} data-id="element-2508" data-mp="12yvmzdv0t338">
                  
                    <Icon className="w-5 h-5 text-white" data-id="element-2509" data-mp="twe3h76r2ojx" />
                  </div>
                  <div data-id="element-2510" data-mp="tyqfz4tqo6pl">
                    <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2511" data-mp="yv7bwl76jpd8">
                      {kpi.value}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 font-medium" data-id="element-2512" data-mp="1ecyny3a825rt">
                      {kpi.label}
                    </p>
                  </div>
                </div>
              </Card>;
      })}
        </div>}

      <ExcelExportModal isOpen={isRevenueExportModalOpen} onClose={() => setIsRevenueExportModalOpen(false)} onExport={(from, to) => handleExportRevenue(from, to, 'excel')} title="Exportar Ingresos a Excel" isExporting={isExportingRevenue} data-id="element-2513" data-mp="10dpi9xjn6u23" />
      

      <PdfExportModal isOpen={isRevenuePdfExportModalOpen} onClose={() => setIsRevenuePdfExportModalOpen(false)} onExport={(from, to) => handleExportRevenue(from, to, 'pdf')} title="Exportar Ingresos a PDF" isExporting={isExportingRevenue} data-id="element-2514" data-mp="qm2yvdx5hoo9" />
      

      {/* Encabezado + botones de exportar Ingresos: visible si puede ver
             estadísticas O si tiene el permiso de exportar Ingresos, aunque no
             tenga acceso a las demás estadísticas */}
      {(canViewStats || canDownloadPdf) && <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-8 mb-4" data-id="element-2515" data-mp="2oaow4yn6jj1b">
        <h2 className="text-xl font-bold text-gray-900 dark:text-white flex items-center gap-2" data-id="element-2516" data-mp="2sctmnoibzzjq">
          <BarChart3Icon className="w-6 h-6 text-teal-600 dark:text-teal-400" data-id="element-2517" data-mp="2xceme15bsbxi" />
          Análisis y Reportes
        </h2>
        {canDownloadPdf && <div className="flex flex-wrap gap-2" data-id="element-2518" data-mp="20jt1zuv94bqw">
          <Button variant="outline" leftIcon={<FileTextIcon className="w-4 h-4" data-id="element-2520" data-mp="24l4q1vyh8owz" />} onClick={() => setIsRevenuePdfExportModalOpen(true)} className="bg-white dark:bg-gray-800" data-id="element-2519" data-mp="24mppnzzney1p">
            
              PDF Ingresos
            </Button>
          <Button variant="outline" leftIcon={<FileSpreadsheetIcon className="w-4 h-4" data-id="element-2522" data-mp="1gvu0lamkugy5" />} onClick={() => setIsRevenueExportModalOpen(true)} className="bg-white dark:bg-gray-800" data-id="element-2521" data-mp="1gyg5t3lzwkd7">
            
              Excel Ingresos
            </Button>
        </div>}
      </div>}

      {/* Stats Charts */}
      {canViewStats && <>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2523" data-mp="mmxeg87202d5">
            <Card padding="none" className="overflow-hidden" data-id="element-2524" data-mp="moici7sku3fr">
              <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-2525" data-mp="rk5xsc3xucfz" />
              <div className="p-4 md:p-6" data-id="element-2526" data-mp="cwch0o3493vt">
                <CardHeader className="px-0 pt-0" data-id="element-2527" data-mp="cv9pcdxyyxeu">
                  <div className="flex items-center justify-between" data-id="element-2528" data-mp="hnkrlng6mvak">
                    <CardTitle className="flex items-center gap-2" data-id="element-2529" data-mp="3vwizu556oyc">
                      <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30" data-id="element-2530" data-mp="3xr2d40rm90b">
                        <TrendingUpIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" data-id="element-2531" data-mp="7y17kegqgve4" />
                      </div>
                      Tendencia Últimos 7 Días
                    </CardTitle>
                    {canDownloadPdf && <button onClick={() => {
                  const cols = [{
                    header: 'Fecha',
                    key: 'fecha'
                  }, {
                    header: 'Total',
                    key: 'total'
                  }, {
                    header: 'Completadas',
                    key: 'completadas'
                  }, {
                    header: 'Programadas',
                    key: 'programadas'
                  }, {
                    header: 'Canceladas',
                    key: 'canceladas'
                  }];
                  const chartRows = appointmentStats.last7Days.map(d => ({
                    label: d.date,
                    values: {
                      fecha: d.date,
                      total: d.total,
                      completadas: d.completadas,
                      programadas: d.programadas,
                      canceladas: d.canceladas
                    }
                  }));
                  exportDashboardChartDataToPdf(chartRows, cols, 'Tendencia Citas Últimos 7 Días');
                }} className="p-2 text-gray-400 hover:text-teal-600 hover:bg-teal-50 dark:hover:bg-teal-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-2532" data-mp="3bgfjsg6ydsh4">
                    
                        <DownloadIcon className="w-4 h-4" data-id="element-2533" data-mp="1hqa5fq5ok72l" />
                      </button>}
                  </div>
                </CardHeader>
                <CardContent className="px-0 pb-0" data-id="element-2534" data-mp="3ek3j8epuq7pa">
                  <ResponsiveContainer width="100%" height={280} data-id="element-2535" data-mp="3jgbuhh0xhgr6">
                    <AreaChart data={appointmentStats.last7Days} data-id="element-2536" data-mp="20lz7ywvurep1">
                      <defs data-id="element-2537" data-mp="20na90oxwy8cg">
                        <linearGradient id="colorCompletadas" x1="0" y1="0" x2="0" y2="1" data-id="element-2538" data-mp="1vvafbe8fsbob">
                        
                          <stop offset="5%" stopColor="#14b8a6" stopOpacity={0.3} data-id="element-2539" data-mp="1mvnd4q7vv1u7" />
                        
                          <stop offset="95%" stopColor="#14b8a6" stopOpacity={0} data-id="element-2540" data-mp="2fiq5t1pvoicj" />
                        
                        </linearGradient>
                        <linearGradient id="colorProgramadas" x1="0" y1="0" x2="0" y2="1" data-id="element-2541" data-mp="25kah15ebpl67">
                        
                          <stop offset="5%" stopColor="#0ea5e9" stopOpacity={0.3} data-id="element-2542" data-mp="u89mf9us32ij" />
                        
                          <stop offset="95%" stopColor="#0ea5e9" stopOpacity={0} data-id="element-2543" data-mp="2mvfqx8foc5wb" />
                        
                        </linearGradient>
                        <linearGradient id="colorCanceladas" x1="0" y1="0" x2="0" y2="1" data-id="element-2544" data-mp="31qlw5t6y40ol">
                        
                          <stop offset="5%" stopColor="#f43f5e" stopOpacity={0.3} data-id="element-2545" data-mp="2rrcyap8ym1wy" />
                        
                          <stop offset="95%" stopColor="#f43f5e" stopOpacity={0} data-id="element-2546" data-mp="1gczmwjve7ext" />
                        
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} vertical={false} data-id="element-2547" data-mp="1duxjck5gy5cs" />
                    
                      <XAxis dataKey="date" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} data-id="element-2548" data-mp="1msq0ltur3n2t" />
                    
                      <YAxis stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} data-id="element-2549" data-mp="q3s70eyqlv78" />
                    
                      <Tooltip contentStyle={{
                    backgroundColor: chartTooltipBg,
                    color: chartTooltipColor,
                    border: chartTooltipBorder,
                    borderRadius: '16px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                    padding: '12px 16px'
                  }} labelStyle={{
                    color: chartTooltipLabelColor
                  }} formatter={(value, name) => [<span style={{
                    fontWeight: 'bold',
                    color: chartTooltipColor
                  }} data-id="element-2551" data-mp="2l6f2zqy0sdgb">
                        
                            {value}
                          </span>, name]} data-id="element-2550" data-mp="z3f1egt1u0qs" />
                    

                      <Legend wrapperStyle={{
                    paddingTop: '20px'
                  }} data-id="element-2552" data-mp="1xf9fqqpr2ivz" />
                    
                      <Area type="monotone" dataKey="completadas" stroke="#14b8a6" strokeWidth={2} fill="url(#colorCompletadas)" name="Completadas" data-id="element-2553" data-mp="38wiy19x574vp" />
                    
                      <Area type="monotone" dataKey="programadas" stroke="#0ea5e9" strokeWidth={2} fill="url(#colorProgramadas)" name="Programadas" data-id="element-2554" data-mp="x84dk3uspcff" />
                    
                      <Area type="monotone" dataKey="canceladas" stroke="#f43f5e" strokeWidth={2} fill="url(#colorCanceladas)" name="Canceladas" data-id="element-2555" data-mp="ik013hmc5x53" />
                    
                    </AreaChart>
                  </ResponsiveContainer>
                </CardContent>
              </div>
            </Card>

            <Card padding="none" className="overflow-hidden" data-id="element-2556" data-mp="23wb8wxrx1n7x">
              <div className="h-1 bg-gradient-to-r from-purple-500 to-indigo-500" data-id="element-2557" data-mp="1yxq8ax8r51iy" />
              <div className="p-4 md:p-6" data-id="element-2558" data-mp="17s4149t4850w">
                <CardHeader className="px-0 pt-0" data-id="element-2559" data-mp="17t5o9cneohyz">
                  <div className="flex items-center justify-between flex-wrap gap-2" data-id="element-2560" data-mp="1crgw5hu54iek">
                    <CardTitle className="flex items-center gap-2" data-id="element-2561" data-mp="y2u88rqg6ibq">
                      <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/30" data-id="element-2562" data-mp="y2kjt8l1wj85">
                        <UserIcon className="w-5 h-5 text-purple-600 dark:text-purple-400" data-id="element-2563" data-mp="130ch6i4gsxjq" />
                      </div>
                      Top Doctores y Terapeutas por Citas
                    </CardTitle>
                    <div className="flex items-center gap-2" data-id="element-2564" data-mp="t52fjvhzas6h">
                      {canDownloadPdf && doctorChartData.length > 0 && <button onClick={() => {
                    const cols = [{
                      header: 'Doctor',
                      key: 'nombre'
                    }, {
                      header: 'Citas',
                      key: 'citas'
                    }];
                    const chartRows = doctorChartData.map(d => ({
                      label: d.name,
                      values: {
                        nombre: d.name,
                        citas: d.citas
                      }
                    }));
                    exportDashboardChartDataToPdf(chartRows, cols, 'Top Doctores y Terapeutas por Citas', {
                      from: doctorDateFrom,
                      to: doctorDateTo
                    });
                  }} className="p-2 text-gray-400 hover:text-purple-600 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-2565" data-mp="k2t7rde3hcws">
                      
                          <DownloadIcon className="w-4 h-4" data-id="element-2566" data-mp="3tb1345dne7zc" />
                        </button>}
                    </div>
                  </div>
                  <div className="flex items-center gap-2 mt-3" data-id="element-2567" data-mp="3odhfiev28iuq">
                    <div className="flex items-center gap-1.5 bg-gray-50 dark:bg-gray-800 p-1 rounded-lg border border-gray-200 dark:border-gray-700" data-id="element-2568" data-mp="742v5bq3raj1">
                      <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={doctorDateFrom} onChange={e => setDoctorDateFrom(e.target.value)} data-id="element-2569" data-mp="26sybb0dvwl6" />
                    
                      <span className="text-gray-300 text-xs" data-id="element-2570" data-mp="kv7q40iizen1">→</span>
                      <input type="date" className="bg-transparent text-xs border-none focus:ring-0 p-1 text-gray-700 dark:text-gray-300" value={doctorDateTo} onChange={e => setDoctorDateTo(e.target.value)} data-id="element-2571" data-mp="kxamr3e6o60q" />
                    
                    </div>
                    <span className="text-xs text-gray-400 dark:text-gray-500" data-id="element-2572" data-mp="1ihai0fuuyuht">
                      {doctorChartData.reduce((sum, d) => sum + d.citas, 0)}{' '}
                      citas
                    </span>
                  </div>
                </CardHeader>
                <CardContent className="px-0 pb-0" data-id="element-2573" data-mp="19hx9bpek69y5">
                  {doctorChartData.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700" data-id="element-2574" data-mp="upd5snnhy0g6">
                      <UserIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-2575" data-mp="lrt1f5uc7o1c" />
                      <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-2576" data-mp="psms26z5zgxx">
                        No hay datos en este rango
                      </p>
                      <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-2577" data-mp="grycv8y1bajg">
                        Ajusta las fechas para ver resultados
                      </p>
                    </div> : <ResponsiveContainer width="100%" height={280} data-id="element-2578" data-mp="2dq49kprp1mf3">
                      <BarChart data={doctorChartData} layout="vertical" data-id="element-2579" data-mp="2dnro42mq8txa">
                        <defs data-id="element-2580" data-mp="29osi0ep9er49">
                          <linearGradient id="barGradient" x1="0" y1="0" x2="1" y2="0" data-id="element-2581" data-mp="29lwjao5l7yer">
                        
                            <stop offset="0%" stopColor="#8b5cf6" data-id="element-2582" data-mp="2xdlimogttn57" />
                            <stop offset="100%" stopColor="#6366f1" data-id="element-2583" data-mp="2ses3yspszq3g" />
                          </linearGradient>
                        </defs>
                        <CartesianGrid strokeDasharray="3 3" stroke={chartGridColor} horizontal={true} vertical={false} data-id="element-2584" data-mp="2nem9l4s5fpho" />
                    
                        <XAxis type="number" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} data-id="element-2585" data-mp="1bz7d023uqzn3" />
                    
                        <YAxis dataKey="name" type="category" stroke="#9ca3af" fontSize={12} tickLine={false} axisLine={false} width={100} data-id="element-2586" data-mp="1bu81nmtw1qdo" />
                    
                        <Tooltip contentStyle={{
                    backgroundColor: chartTooltipBg,
                    color: chartTooltipColor,
                    border: chartTooltipBorder,
                    borderRadius: '16px',
                    boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
                    padding: '12px 16px'
                  }} labelStyle={{
                    color: chartTooltipLabelColor
                  }} formatter={(value: number, name: string) => [<span style={{
                    fontWeight: 'bold',
                    color: chartTooltipColor
                  }} data-id="element-2588" data-mp="1qp4vsumatgyk">
                        
                              {value}
                            </span>, name]} data-id="element-2587" data-mp="1pld5l14ah4tt" />
                    
                        <Bar dataKey="citas" fill="url(#barGradient)" radius={[0, 8, 8, 0]} name="Citas" data-id="element-2589" data-mp="1bx2ztlncn83t" />
                    
                      </BarChart>
                    </ResponsiveContainer>}
                </CardContent>
              </div>
            </Card>
          </div>
        </>}

      {/* Revenue Section */}
      {(canViewRevenueByUser || canViewRevenueByDoctor) && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2590" data-mp="o7s5alwle9y4">
          {canViewRevenueByUser && <DashboardRevenueByUser appointments={appointments} getService={getService} data-id="element-2591" data-mp="2zv3tf4iul7r3" />}
          {canViewRevenueByDoctor && <DashboardRevenueByDoctor appointments={appointments} doctors={doctors} getService={getService} getDoctor={getDoctor} data-id="element-2592" data-mp="2qxl43ny6879e" />}
        </div>}

      {/* Products Revenue Widget */}
      {canViewProductRevenue && <DashboardProductRevenue data-id="element-2593" data-mp="3nmstj71wv0il" />}

      {/* Pipeline */}
      {canViewPipeline && <Card padding="none" className="overflow-hidden" data-id="element-2594" data-mp="3nkgccw3z8u6l">
          <div className="h-1 bg-gradient-to-r from-blue-500 via-purple-500 to-emerald-500" data-id="element-2595" data-mp="39m85nwh52ei8" />
          <div className="p-4 md:p-6" data-id="element-2596" data-mp="3el1irnsik3hv">
            <CardHeader className="px-0 pt-0" data-id="element-2597" data-mp="3eo78945217e4">
              <CardTitle className="flex items-center gap-2" data-id="element-2598" data-mp="1y7l4o4mfvmdx">
                <div className="p-2 rounded-lg bg-gradient-to-br from-blue-100 to-purple-100 dark:from-blue-900/30 dark:to-purple-900/30" data-id="element-2599" data-mp="236xsosp1qzi3">
                  <TrendingUpIcon className="w-5 h-5 text-blue-600 dark:text-blue-400" data-id="element-2600" data-mp="1j3rts37xprwg" />
                </div>
                Pipeline de Ingresos
              </CardTitle>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-2601" data-mp="1abhjklx1hmf7">
              <div className="flex gap-2 overflow-x-auto pb-2" data-id="element-2602" data-mp="1aa6g6n7iagom">
                {pipelineStages.map((stage, index) => {
              const count = getStageCount(stage.id);
              const amount = getStageAmount(stage.id);
              return <div key={stage.id} className="flex items-center" data-id="element-2603" data-mp="veh1zb8kb7ug" data-mpi={index}>
                      <div className={`flex-1 min-w-[140px] p-4 ${stage.bgLight} rounded-xl border border-gray-100 dark:border-gray-700/50 relative overflow-hidden`} data-id="element-2604" data-mp="rd5at1a1iek5">
                      
                        <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r ${stage.color}`} data-id="element-2605" data-mp="2lry0kjo8sb46" />
                      
                        <div className="flex items-center gap-2 mb-2" data-id="element-2606" data-mp="2gqqxgxx1o03u">
                          <div className={`w-2.5 h-2.5 rounded-full bg-gradient-to-r ${stage.color}`} data-id="element-2607" data-mp="2obufb5zmxyg3" />
                        
                          <span className={`text-sm font-semibold ${stage.textColor}`} data-id="element-2608" data-mp="2y1e1akina8f6">
                          
                            {stage.name}
                          </span>
                        </div>
                        <p className="text-2xl font-bold text-gray-900 dark:text-white" data-id="element-2609" data-mp="25f2zj6jay1gs">
                          {count}
                        </p>
                        <p className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2610" data-mp="2fawmljunddct">
                          {formatCurrency(amount)}
                        </p>
                      </div>
                      {index < pipelineStages.length - 1 && <ArrowRightIcon className="w-5 h-5 text-gray-300 dark:text-gray-600 mx-1 flex-shrink-0" data-id="element-2611" data-mp="3qq1t6yxwfw1p" />}
                    </div>;
            })}
              </div>
            </CardContent>
          </div>
        </Card>}

      {/* Appointments by User */}
      {canViewAppointmentsByUser && <DashboardAppointmentsByUser appointments={appointments} data-id="element-2612" data-mp="fneqo3iimynw" />}

      {/* Calendar and Appointments */}
      {canViewCalendar && <div className="grid grid-cols-1 lg:grid-cols-2 gap-6" data-id="element-2613" data-mp="pn5p2ga75xt9">
          <Card padding="none" className="overflow-hidden" data-id="element-2614" data-mp="13etvc2xrls2b">
            <div className="h-1 bg-gradient-to-r from-teal-500 to-cyan-500" data-id="element-2615" data-mp="13d92qiv0gj73" />
            <div className="p-4 md:p-6" data-id="element-2616" data-mp="yepgehci95e7">
              <CardHeader className="px-0 pt-0" data-id="element-2617" data-mp="yhttk47y7bh0">
                <div className="flex items-center justify-between" data-id="element-2618" data-mp="1dd9mg5wsl1bt">
                  <CardTitle className="flex items-center gap-2" data-id="element-2619" data-mp="18dx0rohnq0at">
                    <div className="p-2 rounded-lg bg-teal-100 dark:bg-teal-900/30" data-id="element-2620" data-mp="18eol235t0bls">
                      <CalendarIcon className="w-5 h-5 text-teal-600 dark:text-teal-400" data-id="element-2621" data-mp="3f4wiilvjjmf5" />
                    </div>
                    Calendario
                  </CardTitle>
                  <div className="flex items-center gap-1" data-id="element-2622" data-mp="1poafrqty87i1">
                    <button onClick={prevMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-2623" data-mp="1umc8z4l39n78">
                    
                      <ChevronLeftIcon className="w-5 h-5 text-gray-500" data-id="element-2624" data-mp="1kwiyk6wone4k" />
                    </button>
                    <span className="text-sm font-semibold text-gray-700 dark:text-gray-300 min-w-[130px] text-center" data-id="element-2625" data-mp="16wrfhrrb77jp">
                      {getMonthName(month)} {year}
                    </span>
                    <button onClick={nextMonth} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors" data-id="element-2626" data-mp="wyk0hegkyzkc">
                    
                      <ChevronRightIcon className="w-5 h-5 text-gray-500" data-id="element-2627" data-mp="2rcl8aou3ywwk" />
                    </button>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="px-0 pb-0" data-id="element-2628" data-mp="2wawgyw8o31d7">
                <div className="grid grid-cols-7 gap-1 mb-3" data-id="element-2629" data-mp="1yg38l6ce5k5">
                  {['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'].map(day => <div key={day} className="text-center text-xs font-semibold text-gray-400 dark:text-gray-500 py-2" data-id="element-2630" data-mp="6uoe3mdibcli">
                    
                        {day}
                      </div>)}
                </div>
                <div className="grid grid-cols-7 gap-1" data-id="element-2631" data-mp="llnliu2lvv3i">
                  {calendarDays.map((day, index) => {
                if (day === null) return <div key={`empty-${index}`} className="aspect-square" data-id="element-2632" data-mp="unwuhfi87ges" />;
                const dateStr = getDateString(day);
                const appointmentCount = getAppointmentCountForDay(day);
                const isToday = dateStr === today;
                const isSelected = dateStr === selectedDate;
                return <button key={day} onClick={() => setSelectedDate(dateStr)} className={`aspect-square rounded-xl text-sm font-medium relative flex flex-col items-center justify-center gap-0.5 transition-all duration-200
                          ${isToday ? 'bg-gradient-to-br from-teal-500 to-cyan-500 text-white shadow-lg shadow-teal-500/30' : ''}
                          ${isSelected && !isToday ? 'ring-2 ring-teal-500 ring-offset-2 dark:ring-offset-gray-800 bg-teal-50 dark:bg-teal-900/20 text-teal-700 dark:text-teal-300' : ''}
                          ${!isToday && !isSelected ? 'hover:bg-gray-100 dark:hover:bg-gray-700 text-gray-700 dark:text-gray-300' : ''}`} data-id="element-2633" data-mp="2ttk0d059li8g" data-mpi={index}>
                      
                        <span data-id="element-2634" data-mp="2xuvw7neqezp3">{day}</span>
                        {appointmentCount > 0 && <div className={`w-1.5 h-1.5 rounded-full ${isToday ? 'bg-white' : 'bg-teal-500'}`} data-id="element-2635" data-mp="1rgmrkgc9tt2z" />}
                      </button>;
              })}
                </div>
              </CardContent>
            </div>
          </Card>

          <Card padding="none" className="overflow-hidden" data-id="element-2636" data-mp="igtsvgzyboon">
            <div className="h-1 bg-gradient-to-r from-emerald-500 to-teal-500" data-id="element-2637" data-mp="4ilwba6tupe0" />
            <div className="p-4 md:p-6" data-id="element-2638" data-mp="4kq3c100w4ii">
              <CardHeader className="px-0 pt-0" data-id="element-2639" data-mp="9gdqkauy2nld">
                <CardTitle className="flex items-center gap-2" data-id="element-2640" data-mp="9j9npwyyx88r">
                  <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30" data-id="element-2641" data-mp="3qx53alth32ag">
                    <ClockIcon className="w-5 h-5 text-emerald-600 dark:text-emerald-400" data-id="element-2642" data-mp="3vpgckfa3s0fo" />
                  </div>
                  Citas de {selectedDate === today ? 'Hoy' : selectedDate}
                </CardTitle>
              </CardHeader>
              <CardContent className="px-0 pb-0" data-id="element-2643" data-mp="3mtyvvaumj52e">
                {selectedDateAppointments.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700" data-id="element-2644" data-mp="3cy5o04runzqy">
                    <CalendarIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-2645" data-mp="2td5819cssdtd" />
                    <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-2646" data-mp="2pccw28cjjute">
                      No hay citas programadas
                    </p>
                  </div> : <div className="space-y-3 max-h-[320px] overflow-y-auto pr-1" data-id="element-2647" data-mp="2yb6vs7l651b6">
                    {selectedDateAppointments.map(apt => {
                const patient = getPatient(apt.patientId);
                const doctor = getDoctor(apt.doctorId);
                const service = getService(apt.serviceId);
                return <div key={apt.id} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-800/50 rounded-xl border border-gray-100 dark:border-gray-700/50 relative overflow-hidden" data-id="element-2648" data-mp="3m2vkzf2p19nw">
                      
                          <div className={`absolute left-0 top-0 bottom-0 w-1 ${getStatusAccentColor(apt.status)}`} data-id="element-2649" data-mp="3r1f65d9gqhf7" />
                      
                          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white font-bold text-lg shadow-lg shadow-teal-500/20 flex-shrink-0" data-id="element-2650" data-mp="1tc1o7crdk9qn">
                            {patient?.name?.charAt(0).toUpperCase() || 'P'}
                          </div>
                          <div className="flex-1 min-w-0" data-id="element-2651" data-mp="15fnv73v7qwij">
                            <div className="flex items-center gap-2 mb-1" data-id="element-2652" data-mp="1adpgzwk0x9xp">
                              <span className="text-lg font-bold text-gray-900 dark:text-white" data-id="element-2653" data-mp="1afs2q4e58gwm">
                                {formatTime(apt.time)}
                              </span>
                              <span className="text-sm text-gray-400" data-id="element-2654" data-mp="1kd6ie1s8t1nv">•</span>
                              <span className="text-sm text-gray-500 dark:text-gray-400" data-id="element-2655" data-mp="2qr5rz6usxaxf">
                                {apt.duration} min
                              </span>
                            </div>
                            <p className="font-semibold text-gray-900 dark:text-white truncate" data-id="element-2656" data-mp="k35i9qlfx6iv">
                              {patient?.name || 'Paciente'}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400 truncate" data-id="element-2657" data-mp="ackjo35ro1zy">
                              {doctor ? `${getDoctorPrefix(doctor.gender, getSpecialty(doctor.specialtyId)?.name)} ${doctor.name}` : ''}{' '}
                              • {service?.name}
                            </p>
                          </div>
                          <Badge variant={getStatusBadgeVariant(apt.status)} data-id="element-2658" data-mp="3nl6vygdl9bgy">
                            {getStatusName(apt.status)}
                          </Badge>
                        </div>;
              })}
                  </div>}
              </CardContent>
            </div>
          </Card>
        </div>}

      {/* Recent Appointments */}
      {canViewRecentAppointments && <Card padding="none" className="overflow-hidden" data-id="element-2659" data-mp="3b9eajum1mpp7">
          <div className="h-1 bg-gradient-to-r from-gray-400 to-gray-500" data-id="element-2660" data-mp="3g0mkcrracd2w" />
          <div className="p-4 md:p-6" data-id="element-2661" data-mp="3g2pjc1af1gjr">
            <CardHeader className="px-0 pt-0" data-id="element-2662" data-mp="31crq36xmq0np">
              <div className="flex items-center justify-between" data-id="element-2663" data-mp="319vrdgdyj7y7">
                <CardTitle className="flex items-center gap-2" data-id="element-2664" data-mp="36butyo1vi0u3">
                  <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-700" data-id="element-2665" data-mp="3u0xbzx0f5vp4">
                    <CalendarIcon className="w-5 h-5 text-gray-600 dark:text-gray-400" data-id="element-2666" data-mp="2ucmwitxcxuq" />
                  </div>
                  Citas Recientes
                </CardTitle>
                <div className="flex items-center gap-2" data-id="element-2667" data-mp="3k2832tqi3ll0">
                  {canDownloadPdf && recentAppointments.length > 0 && <button onClick={() => {
                const cols = [{
                  header: 'Paciente',
                  key: 'paciente'
                }, {
                  header: 'Doctor',
                  key: 'doctor'
                }, {
                  header: 'Servicio',
                  key: 'servicio'
                }, {
                  header: 'Fecha',
                  key: 'fecha'
                }, {
                  header: 'Hora',
                  key: 'hora'
                }, {
                  header: 'Estado',
                  key: 'estado'
                }];
                const chartRows = recentAppointmentsMapped.map(apt => ({
                  label: apt.patientName,
                  values: {
                    paciente: apt.patientName,
                    doctor: apt.doctorName,
                    servicio: apt.serviceName,
                    fecha: apt.date,
                    hora: formatTime(apt.time),
                    estado: getStatusName(apt.status)
                  }
                }));
                exportDashboardChartDataToPdf(chartRows, cols, 'Citas Recientes');
              }} className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg transition-colors" title="Descargar PDF" data-id="element-2668" data-mp="28naygbhu2nsj">
                  
                      <DownloadIcon className="w-4 h-4" data-id="element-2669" data-mp="11z3ucfqiurv3" />
                    </button>}
                  {canDownloadPdf && recentAppointments.length > 0 && <button onClick={() => {
                const cols = [{
                  header: 'Paciente',
                  key: 'paciente'
                }, {
                  header: 'Doctor',
                  key: 'doctor'
                }, {
                  header: 'Servicio',
                  key: 'servicio'
                }, {
                  header: 'Fecha',
                  key: 'fecha'
                }, {
                  header: 'Hora',
                  key: 'hora'
                }, {
                  header: 'Estado',
                  key: 'estado'
                }];
                const chartRows = recentAppointmentsMapped.map(apt => ({
                  label: apt.patientName,
                  values: {
                    paciente: apt.patientName,
                    doctor: apt.doctorName,
                    servicio: apt.serviceName,
                    fecha: apt.date,
                    hora: formatTime(apt.time),
                    estado: getStatusName(apt.status)
                  }
                }));
                exportDashboardChartDataToExcel(chartRows, cols, 'Citas Recientes');
              }} className="p-2 text-gray-400 hover:text-green-600 hover:bg-green-50 dark:hover:bg-green-900/20 rounded-lg transition-colors" title="Descargar Excel" data-id="element-2670" data-mp="1at8nx6x1iheb">
                  
                      <FileSpreadsheetIcon className="w-4 h-4" data-id="element-2671" data-mp="10r9qf3njrmif" />
                    </button>}
                  <Button variant="ghost" size="sm" onClick={() => onNavigate('appointments')} className="text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300" data-id="element-2672" data-mp="vxoqh62e8igl">
                  
                    Ver todas
                    <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-2673" data-mp="1jnj0zgz8puxo" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-2674" data-mp="1w0mps1gbjmg0">
              <div className="overflow-x-auto" data-id="element-2675" data-mp="1w1enbhth7u8x">
                <table className="w-full" data-id="element-2676" data-mp="20xmx0fowmv8p">
                  <thead data-id="element-2677" data-mp="21111pul1jgjb">
                    <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-2678" data-mp="25xhlydp4dar4">
                      <SortableHeader label="Paciente" sortKey="patientName" currentDirection={recentIsSorted('patientName')} onSort={recentRequestSort} data-id="element-2679" data-mp="2a04kexgaptxa" />
                    
                      <SortableHeader label="Doctor" sortKey="doctorName" currentDirection={recentIsSorted('doctorName')} onSort={recentRequestSort} data-id="element-2680" data-mp="2oo95u96qgjjr" />
                    
                      <SortableHeader label="Servicio" sortKey="serviceName" currentDirection={recentIsSorted('serviceName')} onSort={recentRequestSort} data-id="element-2681" data-mp="2xpgqvpk8nnvw" />
                    
                      <SortableHeader label="Fecha" sortKey="date" currentDirection={recentIsSorted('date')} onSort={recentRequestSort} data-id="element-2682" data-mp="17d0pcu25fkcz" />
                    
                      <SortableHeader label="Hora" sortKey="time" currentDirection={recentIsSorted('time')} onSort={recentRequestSort} data-id="element-2683" data-mp="1h8u3uizfhnxe" />
                    
                      <SortableHeader label="Estado" sortKey="status" currentDirection={recentIsSorted('status')} onSort={recentRequestSort} data-id="element-2684" data-mp="1m98co38ut1wh" />
                    
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-2685" data-mp="3dwo8qklgewnz">
                    {sortedRecentAppointments.map(apt => <tr key={apt.id} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-2686" data-mp="3hy9ocdpdhp1v">
                    
                        <td className="py-3 px-4" data-id="element-2687" data-mp="395fyjmcav79r">
                          <div className="flex items-center gap-3" data-id="element-2688" data-mp="3944urmiutzin">
                            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-500 flex items-center justify-center text-white text-sm font-semibold" data-id="element-2689" data-mp="2u7nlr1y50wz9">
                              {apt.patientName?.charAt(0).toUpperCase() || 'P'}
                            </div>
                            <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-2690" data-mp="2q8er7uv9wv2n">
                              {apt.patientName}
                            </span>
                          </div>
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-2691" data-mp="2kjdes6np7i91">
                          {apt.doctorName}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-2692" data-mp="2fktq3yhpzscz">
                          {apt.serviceName}
                        </td>
                        <td className="py-3 px-4 text-sm text-gray-600 dark:text-gray-400" data-id="element-2693" data-mp="26kdpqxw9x1cc">
                          {apt.date}
                        </td>
                        <td className="py-3 px-4 text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-2694" data-mp="21mc7ugal382g">
                          {formatTime(apt.time)}
                        </td>
                        <td className="py-3 px-4" data-id="element-2695" data-mp="1ruzgrqwm0lxh">
                          <div className="flex items-center gap-2" data-id="element-2696" data-mp="1mw4wslmworbs">
                            <div className={`w-2 h-2 rounded-full ${getStatusAccentColor(apt.status)}`} data-id="element-2697" data-mp="1mts87pmn7ipr" />
                        
                            <span className="text-sm text-gray-600 dark:text-gray-400" data-id="element-2698" data-mp="1dw9gy3ieo85f">
                              {getStatusName(apt.status)}
                            </span>
                          </div>
                        </td>
                      </tr>)}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </div>
        </Card>}

      {/* Service Detail */}
      {canViewServiceDetail && <Card padding="none" className="overflow-hidden" data-id="element-2699" data-mp="36sc1ethknbeq">
          <div className="h-1 bg-gradient-to-r from-orange-500 to-amber-500" data-id="element-2700" data-mp="36ppoeebww3p4" />
          <div className="p-4 md:p-6" data-id="element-2701" data-mp="3atxfuj9r7xr1">
            <CardHeader className="px-0 pt-0" data-id="element-2702" data-mp="3fsgxi7hbendl">
              <div className="flex items-center justify-between" data-id="element-2703" data-mp="3frp6l9ynxdtn">
                <CardTitle className="flex items-center gap-2" data-id="element-2704" data-mp="3kjqq9gy5u6tp">
                  <div className="p-2 rounded-lg bg-orange-100 dark:bg-orange-900/30" data-id="element-2705" data-mp="3kl1s3b81p4i6">
                    <ListIcon className="w-5 h-5 text-orange-600 dark:text-orange-400" data-id="element-2706" data-mp="2n1qm7rvey4vb" />
                  </div>
                  Detalle por Servicio
                </CardTitle>
                <div className="flex items-center gap-2" data-id="element-2707" data-mp="3vc8xm4s4ul5d">
                  {canDownloadPdf && serviceDetailStats.details.length > 0 && <button onClick={() => {
                exportServiceDetailToPdf(serviceDetailStats.details, serviceDetailStats.grandTotalRevenue, serviceDetailStats.grandTotalCount);
              }} className="p-2 text-gray-400 hover:text-orange-600 hover:bg-orange-50 dark:hover:bg-orange-900/20 rounded-lg transition-colors" title="Descargar PDF" data-id="element-2708" data-mp="3qcw9lgpiz817">
                  
                      <DownloadIcon className="w-4 h-4" data-id="element-2709" data-mp="3gg1d13ctbhpk" />
                    </button>}
                  <Button variant="ghost" size="sm" onClick={() => onNavigate('services')} className="text-orange-600 dark:text-orange-400 hover:text-orange-700 dark:hover:text-orange-300" data-id="element-2710" data-mp="10ovlov6cpfrc">
                  
                    Ver servicios
                    <ArrowRightIcon className="w-4 h-4 ml-1" data-id="element-2711" data-mp="189z6nc48nu7t" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="px-0 pb-0" data-id="element-2712" data-mp="ubhlnm5pwvtl">
              {serviceDetailStats.details.length === 0 ? <div className="text-center py-12 bg-gray-50 dark:bg-gray-800/50 rounded-xl border-2 border-dashed border-gray-200 dark:border-gray-700" data-id="element-2713" data-mp="1mxu1ay1t48nj">
                  <BriefcaseIcon className="w-12 h-12 text-gray-300 dark:text-gray-600 mx-auto mb-3" data-id="element-2714" data-mp="1rvlvxztu68vf" />
                  <p className="text-gray-500 dark:text-gray-400 font-medium" data-id="element-2715" data-mp="1d7hjhdldmtl3">
                    No hay citas completadas aún
                  </p>
                  <p className="text-sm text-gray-400 dark:text-gray-500 mt-1" data-id="element-2716" data-mp="25wy77xy1xmaz">
                    Los datos aparecerán cuando se completen citas
                  </p>
                </div> : <div className="overflow-x-auto" data-id="element-2717" data-mp="al5clzyhzfh1">
                  <table className="w-full" data-id="element-2718" data-mp="alx717gcz75q">
                    <thead data-id="element-2719" data-mp="5uf62jec56kr">
                      <tr className="border-b border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-800/50" data-id="element-2720" data-mp="5u6rmn2kdt7g">
                        <SortableHeader label="Servicio" sortKey="name" currentDirection={serviceIsSorted('name')} onSort={serviceRequestSort} data-id="element-2721" data-mp="w5c2c4ckc0q" />
                    
                        <SortableHeader label="Cantidad" sortKey="count" currentDirection={serviceIsSorted('count')} onSort={serviceRequestSort} align="center" data-id="element-2722" data-mp="okwtq9kb9bz9" />
                    
                        <SortableHeader label="Ingresos Totales" sortKey="totalRevenue" currentDirection={serviceIsSorted('totalRevenue')} onSort={serviceRequestSort} align="right" data-id="element-2723" data-mp="359k7e07jebnc" />
                    
                        <SortableHeader label="Promedio / Cita" sortKey="avgPerAppointment" currentDirection={serviceIsSorted('avgPerAppointment')} onSort={serviceRequestSort} align="right" data-id="element-2724" data-mp="1kr9n24ab2fk2" />
                    
                        <SortableHeader label="% del Total" sortKey="percentOfTotal" currentDirection={serviceIsSorted('percentOfTotal')} onSort={serviceRequestSort} align="right" data-id="element-2725" data-mp="1ylagciihnad1" />
                    
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-100 dark:divide-gray-800" data-id="element-2726" data-mp="e4e4u0sisovc">
                      {sortedServiceDetails.map((service, index) => <tr key={index} className="hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors" data-id="element-2727" data-mp="3qg7wpwkw5y1" data-mpi={index}>
                    
                          <td className="py-3 px-4" data-id="element-2728" data-mp="8j9g5nr99wev">
                            <div className="flex items-center gap-3" data-id="element-2729" data-mp="3qw793apmrozj">
                              <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center text-white text-sm font-semibold" data-id="element-2730" data-mp="3uwhplhb1txsn">
                                {service.name.charAt(0)}
                              </div>
                              <span className="text-sm font-medium text-gray-900 dark:text-gray-100" data-id="element-2731" data-mp="rdy4cyqsfs7c">
                                {service.name}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-center" data-id="element-2732" data-mp="2y68obrigye0o">
                            <span className="inline-flex items-center justify-center min-w-[2rem] px-2 py-0.5 rounded-full bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-300 text-sm font-semibold" data-id="element-2733" data-mp="335leal4n017h">
                              {service.count}
                            </span>
                          </td>
                          <td className="py-3 px-4 text-right text-sm font-semibold text-gray-900 dark:text-gray-100" data-id="element-2734" data-mp="34n0jpotcm6wd">
                            {formatCurrency(service.totalRevenue)}
                          </td>
                          <td className="py-3 px-4 text-right text-sm text-gray-600 dark:text-gray-400" data-id="element-2735" data-mp="2byuvut7001sb">
                            {formatCurrency(service.avgPerAppointment)}
                          </td>
                          <td className="py-3 px-4 text-right" data-id="element-2736" data-mp="2lph6e8zuy245">
                            <div className="flex items-center justify-end gap-2" data-id="element-2737" data-mp="2gwo3zrrv35v8">
                              <div className="w-16 h-1.5 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden" data-id="element-2738" data-mp="1t7btmokssrrc">
                                <div className="h-full bg-gradient-to-r from-orange-500 to-amber-500 rounded-full transition-all duration-500" style={{
                          width: `${Math.min(service.percentOfTotal, 100)}%`
                        }} data-id="element-2739" data-mp="1o9abq6z3yyhg" />
                          
                              </div>
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300 min-w-[3rem] text-right" data-id="element-2740" data-mp="5wismussxyxa">
                                {service.percentOfTotal.toFixed(1)}%
                              </span>
                            </div>
                          </td>
                        </tr>)}
                    </tbody>
                    <tfoot data-id="element-2741" data-mp="3eeoqbkl2hgqc">
                      <tr className="border-t-2 border-gray-300 dark:border-gray-600 bg-gray-50/80 dark:bg-gray-800/80" data-id="element-2742" data-mp="3jbf6s90saeca">
                        <td className="py-3 px-4 text-sm font-bold text-gray-900 dark:text-white" data-id="element-2743" data-mp="tn4s3xoqqavo">
                          TOTAL
                        </td>
                        <td className="py-3 px-4 text-center text-sm font-bold text-gray-900 dark:text-white" data-id="element-2744" data-mp="18pd7a6572gdp">
                          {serviceDetailStats.grandTotalCount}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-900 dark:text-white" data-id="element-2745" data-mp="yzjuj1tbfv7v">
                          {formatCurrency(serviceDetailStats.grandTotalRevenue)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-600 dark:text-gray-400" data-id="element-2746" data-mp="pzep2x7on6wc">
                          {serviceDetailStats.grandTotalCount > 0 ? formatCurrency(serviceDetailStats.grandTotalRevenue / serviceDetailStats.grandTotalCount) : formatCurrency(0)}
                        </td>
                        <td className="py-3 px-4 text-right text-sm font-bold text-gray-900 dark:text-white" data-id="element-2747" data-mp="2ae6vito0mnc6">
                          100%
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>}
            </CardContent>
          </div>
        </Card>}

      {/* No sections visible */}
      {!canViewStats && !canViewCalendar && !canViewPipeline && !canViewAppointmentsByUser && !canViewRevenueByUser && !canViewRevenueByDoctor && !canViewRecentAppointments && !canViewServiceDetail && <Card className="overflow-hidden" data-id="element-2748" data-mp="1unqyajmqwevn">
            <div className="py-16 text-center" data-id="element-2749" data-mp="1zgtuz0mtk4g2">
              <div className="w-20 h-20 mx-auto mb-6 rounded-2xl bg-gradient-to-br from-teal-100 to-cyan-100 dark:from-teal-900/30 dark:to-cyan-900/30 flex items-center justify-center" data-id="element-2750" data-mp="1zin3klgzsyqp">
                <svg xmlns="http://www.w3.org/2000/svg" className="w-10 h-10 text-teal-500" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" data-id="element-2751" data-mp="1praeg1mkwmod">
              
                  <path d="M12 21c-4-4-8-6.5-8-10.5a4.5 4.5 0 0 1 9 0 4.5 4.5 0 0 1 9 0c0 4-4 6.5-8 10.5" data-id="element-2752" data-mp="s563zb2k1crp" />
                  <path d="M12 6c0-2.5 2-4 4-4" data-id="element-2753" data-mp="164pbc1njkk2y" />
                  <path d="M12 6c0-2.5-2-4-4-4" data-id="element-2754" data-mp="166igfciiaw8u" />
                </svg>
              </div>
              <h2 className="text-xl font-bold text-gray-900 dark:text-white mb-2" data-id="element-2755" data-mp="122keku1sr7c3">
                {user?.gender === 'femenino' ? 'Bienvenida' : 'Bienvenido'} al
                Panel de Control
              </h2>
              <p className="text-gray-500 dark:text-gray-400 max-w-md mx-auto" data-id="element-2756" data-mp="1b3s4oou5t1v3">
                No tienes acceso a las secciones avanzadas del panel. Contacta
                al administrador para solicitar permisos adicionales.
              </p>
            </div>
          </Card>}
    </div>;
}